import React from 'react';
import { parseISO } from 'date-fns';

import useToggle from '@lyearn/core/hooks/useToggle';
import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import { Trans, useTranslation } from '@lyearn/i18n';
import { DialogEntity } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Checkbox, Stack, Typography } from '@lyearn/ui';
import { Dialog } from '@/components/Modals';
import { getDescString } from '@/modules/1on1s/views/1on1Details/components/Header/utils';
import { EventStatusEnum, EventUnion, UpdateEventStatusTypeEnum } from '@/types/schema';

import { getIsRecurringSession, getIsRecurringSessionInstance } from '../../../../utils';
import useRecurringInstanceEvents from '../../../EventDetails/components/RecurringInstanceSidebar/useRecurringInstanceEvents';
import { DialogActions } from '../../types';
import { getUsersName } from '../../utils';
import { sessionNameRootStyles } from '../ListItemContainer/styles';

function DeleteSessionDialog({
  Image,
  onAction,
  event,
}: {
  Image: JSX.Element;
  onAction: (props: {
    dialogAction: DialogActions;
    eventName?: string;
    message?: string;
    updateType?: UpdateEventStatusTypeEnum;
  }) => void;
  event: ReturnType<typeof useRecurringInstanceEvents>['events'][0] | EventUnion;
}) {
  const { t } = useTranslation();
  const isRecurringInstance = getIsRecurringSessionInstance(event.recurringCategory);
  const isRecurring = getIsRecurringSession(event.recurringCategory);
  const [deleteAllSlots, { toggle: toggleDeleteAllSlots }] = useToggle(false);
  const onSubmit = React.useCallback(() => {
    onAction({
      dialogAction: DialogActions.Delete,
      eventName: event.name,
      updateType: deleteAllSlots ? UpdateEventStatusTypeEnum.All : undefined,
    });
  }, [deleteAllSlots, event.name, onAction]);

  const getDescriptionNode = () => {
    return (
      <Typography color="text-text-secondary" variant="body-short-02">
        {isRecurring ? (
          getDescString(t, event.recurrenceSchedule!)
        ) : event.instructors.length ? (
          <Trans
            i18nKey="Admin:Contents.DeleteSessionDescription"
            values={{
              userName: getUsersName(t, event.instructors),
              date: formatDate(parseISO(event.startTime), DateFormat.Date),
            }}
          />
        ) : (
          `on ${formatDate(parseISO(event.startTime), DateFormat.Date)}`
        )}
      </Typography>
    );
  };

  return (
    <Dialog
      action={{
        appearance: 'danger',
        text: t('Common:Actions.Delete'),
        discardText: t('Common:Actions.Cancel'),
        onSubmit: onSubmit,
        className: '!pt-16 !pb-16 !mt-20',
      }}
      header={{
        title: isRecurring
          ? t('Events:Manager.DeleteRecurringSession')
          : t('Events:Manager.DeleteSession'),
        description: t('Events:Manager.DeleteSessionDescription'),
        className: 'pt-28',
        classes: {
          description: 'pe-8',
        },
      }}
      onClose={() => onAction({ dialogAction: DialogActions.Close })}>
      <Stack className="px-24" direction="col" gapY={isRecurringInstance ? 24 : 8}>
        <DialogEntity
          className="mt-0"
          descriptionNode={getDescriptionNode()}
          image={Image}
          title={
            <Typography
              className={cn(sessionNameRootStyles, 'truncate')}
              color="text-text-primary"
              data-testid="title"
              variant="label-01">
              {event.name}
            </Typography>
          }
        />
      </Stack>
      {isRecurring && event.status != EventStatusEnum.Completed && (
        <Stack className="px-24 mt-24 gap-8">
          <Checkbox checked={deleteAllSlots} onChange={toggleDeleteAllSlots} />
          <Typography variant="body-short-02">{t('Admin:Contents.DeleteAllSlots')}</Typography>
        </Stack>
      )}
    </Dialog>
  );
}

export default React.memo(DeleteSessionDialog);
